/**
 * Landing-page attribution for leads and booking requests.
 *
 * The question this answers: "which pages generate qualified enquiries?" Two
 * different facts are kept, because they answer different questions:
 *
 *   firstTouch  - where the visitor FIRST arrived (path, referrer host, UTM
 *                 tags). Tells us which pages and channels bring people in.
 *   cameFrom    - the last page they viewed before opening /contact. Tells us
 *                 which page convinced them to ask.
 *
 * What is stored, and only this: page paths (never full URLs with queries), the
 * referrer's hostname (never its path or query), and the three standard UTM
 * values. No name, email, IP or anything about the person. It lives in this
 * browser (localStorage for the first touch, sessionStorage for the last page)
 * and leaves only when the visitor submits the form or a booking request.
 * Every read and write is wrapped because storage can be blocked, and failure
 * must never affect the enquiry itself.
 */

export type FirstTouch = {
  landingPage: string;
  referrerHost: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  /** Keyword and ad-group tags from the campaign link. Optional: older records do not have them. */
  utmTerm?: string;
  utmContent?: string;
  at: number;
};

/**
 * A Google Ads click identifier from the address the visitor arrived on. It is what lets a later customer or
 * qualified lead be traced back to the campaign and keyword that paid for the click. It identifies a click,
 * not a person, but it is still a tracking identifier, so it is stored ONLY while advertising storage is
 * consented to (see src/lib/consent.ts), and only for 90 days.
 */
export type AdClick = { type: 'gclid' | 'gbraid' | 'wbraid'; id: string; at: number };

export type Attribution = {
  /** The last call-to-action clicked this session, as "/page (cta-id)", or ''. A click is interest, never a sale. */
  entryCta: string;
  adClick: AdClick | null;
  firstTouch: FirstTouch | null;
  /** The last non-contact page viewed this session, or '' if they came straight to /contact. */
  cameFrom: string;
};

const FIRST_KEY = 'kit_first_touch';
const CTA_KEY = 'kit_entry_cta';
const PREV_KEY = 'kit_prev_page';
const AD_KEY = 'kit_ad_click';
const NINETY_DAYS = 1000 * 60 * 60 * 24 * 90;
const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
const MAX = 200;

const clip = (v: string, n = MAX) => v.trim().slice(0, n);

/** Keeps only a path: no query string, no hash, no host. */
export function cleanPath(p: string): string {
  const path = (p || '/').split('?')[0].split('#')[0];
  return clip(path.startsWith('/') ? path : `/${path}`);
}

function hostOf(referrer: string): string {
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, '');
    // Our own pages are not a "referrer"; that is just navigation.
    return host === location.hostname.replace(/^www\./, '') ? '' : host;
  } catch {
    return '';
  }
}

/** Reads the first-touch record, ignoring one older than 30 days. */
function readFirst(): FirstTouch | null {
  try {
    const raw = localStorage.getItem(FIRST_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as FirstTouch;
    return v && typeof v.at === 'number' && Date.now() - v.at < THIRTY_DAYS ? v : null;
  } catch {
    return null;
  }
}

/** Call on every navigation. Records the first touch once, and the previous page each time. */
export function recordPageView(pathname: string) {
  // Where consent is required and not given, nothing at all is kept about the visit.
  if (!analyticsGranted()) return;
  try {
    const path = cleanPath(pathname);

    if (!readFirst()) {
      const q = new URLSearchParams(location.search);
      const first: FirstTouch = {
        landingPage: path,
        referrerHost: hostOf(document.referrer),
        utmSource: clip(q.get('utm_source') || '', 100),
        utmMedium: clip(q.get('utm_medium') || '', 100),
        utmCampaign: clip(q.get('utm_campaign') || '', 100),
        utmTerm: clip(q.get('utm_term') || '', 100) || undefined,
        utmContent: clip(q.get('utm_content') || '', 100) || undefined,
        at: Date.now(),
      };
      localStorage.setItem(FIRST_KEY, JSON.stringify(first));
      // Once per visitor: the channel as its own GA dimension, so AI-assistant traffic is
      // countable even when the visitor never sends an enquiry. Only fixed labels and hosts.
      const gtag = (window as unknown as { gtag?: Gtag }).gtag;
      if (typeof gtag === 'function') {
        gtag('event', 'first_touch', { channel: channelOf(first), source: first.utmSource || first.referrerHost || '(direct)', landing_page: path });
      }
    }

    // The contact page is the destination, not the page that persuaded them.
    if (path !== '/contact') sessionStorage.setItem(PREV_KEY, path);
  } catch {
    /* storage blocked: the enquiry simply goes without attribution */
  }
  captureAdClick();
}

/** True while the visitor's consent state allows analytics storage. Unknown means no. */
export function analyticsGranted(): boolean {
  try {
    return (window as unknown as { __kitConsent?: { analytics_storage?: string } }).__kitConsent?.analytics_storage === 'granted';
  } catch {
    return false;
  }
}

/** True while the Google tag has been told advertising storage is allowed. Unknown means no. */
export function adStorageGranted(): boolean {
  try {
    return (window as unknown as { __kitConsent?: { ad_storage?: string } }).__kitConsent?.ad_storage === 'granted';
  } catch {
    return false;
  }
}

/**
 * Keeps the most recent Google Ads click id from the current address, if the visitor allows it. Called on each
 * page view and again when consent changes, because the id is only in the address on the landing page.
 */
export function captureAdClick() {
  try {
    if (!adStorageGranted()) return;
    const q = new URLSearchParams(location.search);
    for (const type of ['gclid', 'gbraid', 'wbraid'] as const) {
      const id = (q.get(type) || '').trim();
      if (id && /^[A-Za-z0-9_-]{8,200}$/.test(id)) {
        localStorage.setItem(AD_KEY, JSON.stringify({ type, id, at: Date.now() } satisfies AdClick));
        return;
      }
    }
  } catch {
    /* storage blocked */
  }
}

/** Forgets the stored click id, for example when the visitor withdraws consent. */
export function forgetAdClick() {
  try {
    localStorage.removeItem(AD_KEY);
  } catch {
    /* ignore */
  }
}

function readAdClick(): AdClick | null {
  try {
    const v = JSON.parse(localStorage.getItem(AD_KEY) || 'null') as AdClick | null;
    // A click id is only ever sent along while consent still holds.
    return v && typeof v.id === 'string' && Date.now() - v.at < NINETY_DAYS && adStorageGranted() ? v : null;
  } catch {
    return null;
  }
}

export function getAttribution(): Attribution {
  let cameFrom = '';
  let entryCta = '';
  try {
    cameFrom = sessionStorage.getItem(PREV_KEY) || '';
    entryCta = sessionStorage.getItem(CTA_KEY) || '';
  } catch {
    /* ignore */
  }
  return { firstTouch: readFirst(), cameFrom, entryCta, adClick: readAdClick() };
}

/**
 * Remembers which call-to-action sent the visitor toward the contact flow, so
 * the enquiry can say "from the hero button on /hvac-website-design". Only a
 * page path and a short fixed id are kept, in this tab's sessionStorage.
 */
export function recordCta(page: string, cta: string) {
  if (!analyticsGranted()) return;
  try {
    sessionStorage.setItem(CTA_KEY, clip(`${cleanPath(page)} (${cta.replace(/[^a-z0-9-]/gi, '').slice(0, 40)})`));
  } catch {
    /* ignore */
  }
}

/**
 * Which kind of place a visitor came from, worked out from the UTM tags and
 * the referrer host only. AI assistants get their own channel: ChatGPT adds
 * `utm_source=chatgpt.com` to the links it cites, and Perplexity, Gemini,
 * Copilot and Claude show up as referrers, so "who found us through AI" is
 * answerable per lead.
 */
export type Channel = 'AI assistant' | 'Search' | 'Maps' | 'Social' | 'Email' | 'Paid' | 'Referral' | 'Direct';

const AI_HOSTS = /(^|\.)(chatgpt\.com|chat\.openai\.com|openai\.com|perplexity\.ai|gemini\.google\.com|bard\.google\.com|copilot\.microsoft\.com|claude\.ai|you\.com|phind\.com|poe\.com|meta\.ai|grok\.com|deepseek\.com)$/i;
const SEARCH_HOSTS = /(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|search\.yahoo\.com|yahoo\.com|ecosia\.org|search\.brave\.com|yandex\.[a-z]+|baidu\.com)$/i;
const MAPS_HOSTS = /(^|\.)(maps\.google\.[a-z.]+|maps\.apple\.com|goo\.gl)$/i;
const SOCIAL_HOSTS = /(^|\.)(facebook\.com|fb\.com|instagram\.com|tiktok\.com|youtube\.com|youtu\.be|linkedin\.com|lnkd\.in|x\.com|t\.co|twitter\.com|pinterest\.com|reddit\.com|threads\.net|nextdoor\.com|whatsapp\.com)$/i;

export function channelOf(f: Pick<FirstTouch, 'utmSource' | 'utmMedium' | 'referrerHost'> | null, adClick?: AdClick | null): Channel {
  if (!f) return adClick ? 'Paid' : 'Direct';
  const source = (f.utmSource || '').toLowerCase();
  const medium = (f.utmMedium || '').toLowerCase();
  const host = (f.referrerHost || '').toLowerCase();
  const any = (re: RegExp) => re.test(source) || re.test(host);
  if (any(AI_HOSTS) || /^(chatgpt|openai|perplexity|gemini|copilot|claude)$/.test(source) || medium === 'ai') return 'AI assistant';
  if (adClick || /^(cpc|ppc|paid|paidsearch|paid_social|paidsocial|display|ads?)$/.test(medium)) return 'Paid';
  if (medium === 'email' || medium === 'newsletter') return 'Email';
  if (any(MAPS_HOSTS) || medium === 'gbp' || source === 'gbp' || source === 'google_business_profile') return 'Maps';
  if (any(SOCIAL_HOSTS) || medium === 'social' || /^(facebook|instagram|tiktok|youtube|linkedin|x|twitter|pinterest)$/.test(source)) return 'Social';
  if (any(SEARCH_HOSTS) || medium === 'organic') return 'Search';
  if (source || host) return 'Referral';
  return 'Direct';
}

/** Structured fields the leads API stores natively on the lead. */
export function leadAttributionFields(a: Attribution = getAttribution()) {
  const f = a.firstTouch;
  const channel = channelOf(f, a.adClick);
  return {
    landingPage: f?.landingPage || undefined,
    utmSource: f?.utmSource || f?.referrerHost || undefined,
    // With no medium in the link, the channel stands in for it, so "AI assistant" is filterable in the CRM.
    utmMedium: f?.utmMedium || (channel !== 'Direct' ? channel.toLowerCase().replace(/\s+/g, '-') : undefined),
    utmCampaign: f?.utmCampaign || undefined,
  };
}

/** The same facts as readable lines, for the lead message and booking notes. */
export function attributionLines(a: Attribution = getAttribution()): string[] {
  const f = a.firstTouch;
  const lines: string[] = [];
  lines.push(`Came from page: ${a.cameFrom || '(went straight to contact)'}`);
  if (a.entryCta) lines.push(`Entry button: ${a.entryCta}`);
  lines.push(`Channel: ${channelOf(f, a.adClick)}${f?.utmSource || f?.referrerHost ? ` (${f.utmSource || f.referrerHost})` : ''}`);
  if (f) {
    lines.push(`First landing page: ${f.landingPage}`);
    const source = [f.utmSource || f.referrerHost || 'direct', f.utmMedium, f.utmCampaign].filter(Boolean).join(' / ');
    lines.push(`Source: ${source}`);
    if (f.utmTerm) lines.push(`Campaign keyword: ${f.utmTerm}`);
    if (f.utmContent) lines.push(`Campaign content: ${f.utmContent}`);
  }
  // Only present while advertising storage is allowed. It is what lets Google Ads credit a later customer.
  if (a.adClick) lines.push(`Google Ads click id (${a.adClick.type}): ${a.adClick.id}`);
  return lines;
}

type Gtag = (command: 'event', name: string, params: Record<string, unknown>) => void;

/**
 * Sends a Google Analytics event so conversions can be broken down by page in
 * GA as well as in the CRM. Safe when the tag is blocked or not loaded.
 */
/** One random id per form submission, shared by the events it causes. It identifies the submission, not the person. */
export function newSubmissionId(): string {
  try {
    return globalThis.crypto?.randomUUID?.() ?? `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  } catch {
    return `s-${Date.now()}`;
  }
}

/**
 * These are the only two events that may ever be marked as conversions, and they are sent ONLY after the server
 * has accepted the enquiry (never on a click, a page view or a failed send). Parameters are fixed ids and
 * page paths: no name, email, phone, message or website address.
 */
export function trackGaConversion(event: 'generate_lead' | 'booking_request', extra: Record<string, unknown> = {}) {
  // Events queued before consent would be sent after the visitor accepts, so none are queued without it.
  if (!analyticsGranted()) return;
  try {
    const gtag = (window as unknown as { gtag?: Gtag }).gtag;
    if (typeof gtag !== 'function') return;
    const a = getAttribution();
    gtag('event', event, {
      came_from_page: a.cameFrom || '(direct to contact)',
      entry_cta: a.entryCta || '(none)',
      first_landing_page: a.firstTouch?.landingPage || '(unknown)',
      first_touch_channel: channelOf(a.firstTouch, a.adClick),
      ...extra,
    });
  } catch {
    /* analytics must never affect the visitor */
  }
}

/**
 * A call-to-action was clicked. This is interest, not a lead and not a sale:
 * it is its own GA event (`cta_click`) and never `generate_lead`. No name,
 * email or free text is ever attached; only fixed ids and page paths.
 */
export function trackCta(cta: string, page: string, destination: string) {
  recordCta(page, cta);
  if (!analyticsGranted()) return;
  try {
    const gtag = (window as unknown as { gtag?: Gtag }).gtag;
    if (typeof gtag !== 'function') return;
    gtag('event', 'cta_click', {
      cta_id: cta.slice(0, 40),
      page_path: cleanPath(page),
      destination_path: cleanPath(destination),
    });
  } catch {
    /* analytics must never affect the visitor */
  }
}
